import React, { useEffect, useState } from "react";
import { Alert, Card, Spin, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { AuthUser } from "../../../context/AuthContext";
import { disablePush, enablePush, getPushSupport, loadPushPreferences, PushUnavailableError, type PushSupport } from "../../../services/pushService";

export const PushSettings: React.FC<{ user: AuthUser }> = ({ user }) => {
    const { t } = useTranslation();
    const [support] = useState<PushSupport>(getPushSupport);
    const [enabled, setEnabled] = useState(false);
    const [recentWindowMinutes, setRecentWindowMinutes] = useState(180);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");
    const [unavailable, setUnavailable] = useState(false);

    useEffect(() => {
        let active = true;
        void loadPushPreferences(user).then((preferences) => {
            if (!active) return;
            setEnabled(preferences.enabled);
            setRecentWindowMinutes(preferences.recentWindowMinutes);
        }).catch((reason: unknown) => {
            if (active) {
                if (reason instanceof PushUnavailableError) setUnavailable(true);
                else setError(t("profile.push.loadError"));
            }
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [t, user]);

    const changeEnabled = async (next: boolean) => {
        setSaving(true);
        setError("");
        setStatus("");
        try {
            if (next) await enablePush(user, recentWindowMinutes);
            else await disablePush(user, recentWindowMinutes);
            setEnabled(next);
            setStatus(t(next ? "profile.push.enabled" : "profile.push.disabled"));
        } catch (reason) {
            setError(t(reason instanceof Error && reason.message === "Push permission denied" ? "profile.push.denied" : "profile.push.error"));
        } finally {
            setSaving(false);
        }
    };

    let preferencesContent: React.ReactNode;
    if (unavailable) {
        preferencesContent = <Typography.Paragraph role="status">{t("profile.push.unavailable")}</Typography.Paragraph>;
    } else if (loading) {
        preferencesContent = <Typography.Paragraph role="status"><Spin size="small" /> {t("profile.push.loading")}</Typography.Paragraph>;
    } else {
        preferencesContent = (
            <label style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 44, cursor: support === "supported" ? "pointer" : "default" }}>
                <Switch
                    checked={enabled}
                    loading={saving}
                    disabled={(support !== "supported" && !enabled) || loading || saving}
                    onChange={(checked) => { void changeEnabled(checked); }}
                    aria-label={t("profile.push.switchLabel")}
                />
                <span>{t("profile.push.switchLabel")}</span>
            </label>
        );
    }

    return (
        <Card className="profile-push-settings" title={t("profile.push.title")}>
            <Typography.Paragraph>{t("profile.push.description")}</Typography.Paragraph>
            {support !== "supported" && (
                <Alert type="warning" showIcon message={t(support === "install-required" ? "profile.push.installRequired" : "profile.push.unsupported")} />
            )}
            {preferencesContent}
            {error && <Alert role="alert" type="error" showIcon message={error} />}
            {status && <Typography.Paragraph role="status" aria-live="polite">{status}</Typography.Paragraph>}
        </Card>
    );
};
